import {Button, Typography} from "@mui/material";
import {Box} from "@mui/system";
import {DataGrid, GridActionsCellItem, GridColDef, GridToolbar, GridValueGetterParams, plPL} from '@mui/x-data-grid';
import React, {FC, useEffect, useState} from "react";
import {LoadingComponent} from "../../../components/LoadingComponent";
import {ClimbigEventType, ClimbingEvent} from "../../../models/ClimbingEvent";
import {recalculateScore, UserClimbingEvent, UserRouteStatus} from "../../../models/UserClimbingEvent";
import {getSexAsLetter, Sex} from "../../../models/UserData";
import {calculateAndUpdateSimplifiedResults} from "../../../services/events/EventSimplifiedResultService";
import {getAllUserEvents, updateUserEvent} from "../../../services/events/UserEventService";
import {useTranslation} from "react-i18next";
import EditIcon from '@mui/icons-material/Edit';
import {ModalComponent} from "../../../components/Modal/ModalComponent";
import {EditUserResults} from "./user/EditUserResults";
import {CreateNewUser} from "./user/CreateNewUser";

type Props = {
    event: ClimbingEvent
}

type RowValues = {
    id: string,
    name: string,
    email: string,
    birthYear: number,
    category: string,
    sex?: Sex,
    isSection: boolean,
    section: string,
    sumOfPoints: number,
    sumOfZones?: number,
    group: string,
    startNumber: string,
    sponsor: string,
    city: string,
    doneRoutes: string,
    flashRoutes: string,
}

export const ViewEventResultsComponent: FC<Props> = ({ event }) => {
    const {t} = useTranslation()

    const [values, setValues] = useState<RowValues[]>([]);
    const [climbingEvents, setClimbingEvents] = useState<UserClimbingEvent[]>()
    const [userToEdit, setUserToEdit] = useState<UserClimbingEvent>()
    const [addUser,setAddUser] = useState(false)

    useEffect(() => {
        getAllUserEvents(event.name).then(setClimbingEvents)
    }, [event])

    useEffect(() => {
        if (climbingEvents) {
            calculateAndUpdateSimplifiedResults(climbingEvents)
        }
    }, [climbingEvents, event])

    useEffect(() => {
        if (climbingEvents) {
            climbingEvents.forEach(climbingEvent => { recalculateScore(climbingEvent, event) })
            const rows = climbingEvents.map<RowValues>(climbingEvent => {
                return {
                    id: climbingEvent.userId,
                    name: climbingEvent.name || "",
                    email: climbingEvent.email || "",
                    birthYear: climbingEvent.birthYear,
                    category: climbingEvent.category?.toString() || "",
                    sex: climbingEvent.sex,
                    section: climbingEvent.isSection ? (climbingEvent.section ? climbingEvent.section : "TAK") : "Nie",
                    isSection: climbingEvent.isSection,
                    sumOfPoints: climbingEvent.sumOfPoints,
                    sumOfZones: climbingEvent.sumOfZones,
                    group: climbingEvent.competitionUserData?.group?.name || "",
                    sponsor: climbingEvent.competitionUserData?.sponsorName || "",
                    city: climbingEvent.competitionUserData?.city || "",
                    zoneRoutes: climbingEvent.climbingRoutes?.filter(r=> r.status === UserRouteStatus.ZONE).map(route => route.id).join(", ") || "",
                    zone2Routes: climbingEvent.climbingRoutes?.filter(r=> r.status === UserRouteStatus.ZONE2).map(route => route.id).join(", ") || "",
                    doneRoutes: climbingEvent.climbingRoutes?.filter(r=> r.status === UserRouteStatus.DONE).map(route => route.id).join(", ") || "",
                    flashRoutes: climbingEvent.climbingRoutes?.filter(r=> r.status === UserRouteStatus.FLASH).map(route => route.id).join(", ") || "",
                    bestRoute: climbingEvent.bestRouteId || "",
                    startNumber: climbingEvent.startNumber || "",
                }
            })
            setValues(rows);
        }
    }, [climbingEvents,event])

    const editUser = (uid: string) => () => setUserToEdit(climbingEvents?.find(ce => ce.userId === uid))

    const saveUser = (user: UserClimbingEvent) => {
        setUserToEdit(undefined)
        updateUserEvent(user,user.userId).then(() => {getAllUserEvents(event.name).then(setClimbingEvents)})
    }

    if (climbingEvents === undefined) return <LoadingComponent message="Pobieranie wyników..." />

    const columns : GridColDef<RowValues>[] = [
          {
              field: 'actions',
              type: 'actions',
              width: 80,
              getActions: (params) => [
                  <GridActionsCellItem
                    icon={<EditIcon />}
                    label="Edycja"
                    onClick={editUser(params.row.id)}
                  />
              ],
          },
        { field: 'name', headerName: 'Imię i nazwisko', maxWidth: 300, minWidth: 150, flex: 1 },
        { field: 'email', headerName: 'E-mail', flex: 1, width: 150 },
        { field: 'birthYear', headerName: 'Rok', type: 'number', flex: 1, maxWidth: 60 },
        {
            field: 'sex',
            headerName: 'Płeć',
            valueGetter: (params: GridValueGetterParams) => t(`user.gender.${getSexAsLetter(params.row.sex)}`),
            flex: 1, maxWidth: 60
        },
        { field: 'category', headerName: 'Kategoria', flex: 1, maxWidth: 80 },
        { field: 'sumOfPoints', headerName: 'Punkty', flex: 1, maxWidth: 80 },
    ]
    if(event.separateZonePoint){
        columns.push({ field: 'sumOfZones', headerName: 'Zony', flex: 1, maxWidth: 80 })
    }else if(event.separateFlashPoint){
        columns.push({ field: 'sumOfZones', headerName: 'Flashe', flex: 1, maxWidth: 80 })
    }

    if (event.allowSection) {
        columns.push({ field: 'isSection', headerName: 'Sekcja', flex: 1, maxWidth: 80 })
        columns.push({ field: 'section', headerName: 'Nazwa sekcji', flex: 1, maxWidth: 80 })
    }

    if (event.type !== ClimbigEventType.BOULDER) {
        columns.push({ field: 'group', headerName: 'Grupa', flex: 1, maxWidth: 80 })
        columns.push({ field: 'sponsor', headerName: 'Klub/Sponsor', flex: 1, maxWidth: 80 })
        columns.push({ field: 'city', headerName: 'Miasto', flex: 1, maxWidth: 80 })
    }
    if(event.collectUserId){
        columns.push({ field: 'startNumber', headerName: 'Numer startowy', flex: 1, maxWidth: 80 })
    }
    columns.push({ field: 'zoneRoutes', headerName: 'Zoony', flex: 1, maxWidth: 80 })
    columns.push({ field: 'zone2Routes', headerName: 'Zoony 2', flex: 1, maxWidth: 80 })
    columns.push({ field: 'doneRoutes', headerName: 'Zrobione drogi', flex: 1, maxWidth: 80 })
    columns.push({ field: 'flashRoutes', headerName: 'Flashe', flex: 1, maxWidth: 80 })
    columns.push({ field: 'bestRoute', headerName: 'Ulubiona droga', flex: 1, maxWidth: 80 })

    if (values.length === 0) return <Typography align="center" variant="h6">Brak wyników</Typography>
    return (
        <Box>
            <Button onClick={()=>setAddUser(true)} variant="contained" color="primary">Dodaj nowego użytkownika</Button>
            <div style={{ height: 1200, width: '100%' }}>
                <DataGrid
                    columnBuffer={8}
                    localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                        toolbar: {
                            csvOptions: {
                                fileName: `wyniki-${event.name}-apka`,
                                delimiter: ';',
                                utf8WithBom: true,
                            }
                        }
                    }
                    }
                    rows={values}
                    columns={columns}
                    pageSizeOptions={[5, 10, 20, 100, 200]}
                    checkboxSelection
                />
            </div>
            {userToEdit &&  <ModalComponent childComp={<EditUserResults event={event} userEvent={userToEdit} updateUserData={(u)=>saveUser(u)}/>} open={!!userToEdit} onClose={()=>setUserToEdit(undefined)}/>}
            {addUser &&  <ModalComponent childComp={<CreateNewUser event={event} postCreate={(u)=>{setAddUser(false); setUserToEdit(u) }} /> } open={addUser} onClose={()=>setAddUser(false)}/>}

        </Box>
    )
}